import {
  Box,
  Modal as ChakraModal,
  Flex,
  IconButton,
  IconButtonProps,
  ModalBody,
  ModalCloseButtonProps,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalFooter,
  ModalProps,
  Show,
} from '@chakra-ui/react';
import { IconInfo, IconUpgradeNew, IconClose } from 'icons';

const modalVariantColors = {
  green: {
    overlayBg: {
      base: 'linear-gradient(319.38deg, rgba(0, 255, 232, 0) 69.1%, rgba(0, 255, 232, 0.3) 79.95%, rgba(0, 255, 232, 0.8) 96.24%)',
      md: 'linear-gradient(356.38deg, rgba(0, 255, 232, 0) 69.1%, rgba(0, 255, 232, 0.3) 79.95%, rgba(0, 255, 232, 0.8) 96.24%)',
    },
    iconBg: 
      'linear-gradient(302.62deg, rgba(0, 255, 232, 0.06) 0%, rgba(0, 255, 232, 0.12) 50%, rgba(0, 255, 232, 0.06) 100%)',
    iconBorderColor:
      'linear-gradient(91.01deg, rgba(0, 255, 232, 0.6) 0.87%, rgba(0, 255, 232, 0) 30.35%, rgba(0, 255, 232, 0) 69.65%, rgba(0, 255, 232, 0.6) 99.13%)',
  },
  blue: {
    overlayBg: {
      base: 'linear-gradient(330.38deg, rgba(42, 102, 255, 0) 69.1%, rgba(42, 102, 255, 0.3) 79.95%, rgba(42, 102, 255, 0.8) 96.24%)',
      md: 'linear-gradient(356.38deg, rgba(42, 102, 255, 0) 69.1%, rgba(42, 102, 255, 0.3) 79.95%, rgba(42, 102, 255, 0.8) 96.24%)',
    },
    iconBg:
      'linear-gradient(302.62deg, rgba(42, 102, 255, 0.06) 0%, rgba(42, 102, 255, 0.12) 50%, rgba(42, 102, 255, 0.06) 100%)',
    iconBorderColor:
      'linear-gradient(91.01deg, rgba(42, 102, 255, 0.6) 0.87%, rgba(42, 102, 255, 0) 30.35%, rgba(42, 102, 255, 0) 69.65%, rgba(42, 102, 255, 0.6) 99.13%)',    
  },
  red: {
    overlayBg: {
      base: 'linear-gradient(319.38deg, rgba(249, 57, 57, 0) 69.1%, rgba(249, 57, 57, 0.3) 79.95%, rgba(249, 57, 57, 0.8) 96.24%)',
      md: 'linear-gradient(356.38deg, rgba(249, 57, 57, 0) 69.1%, rgba(249, 57, 57, 0.3) 79.95%, rgba(249, 57, 57, 0.8) 96.24%)',
    },
    iconBg:
      'linear-gradient(302.62deg, rgba(249, 57, 57, 0.06) 0%, rgba(249, 57, 57, 0.12) 50%, rgba(249, 57, 57, 0.06) 100%)',
    iconBorderColor:
      'linear-gradient(91.01deg, rgba(249, 57, 57, 0.6) 0.87%, rgba(249, 57, 57, 0) 30.35%, rgba(249, 57, 57, 0) 69.65%, rgba(249, 57, 57, 0.6) 99.13%)',
  },
  golden: {
    overlayBg: {
      base: 'linear-gradient(319.38deg, rgba(255, 218, 44, 0) 69.1%, rgba(255, 218, 44, 0.3) 79.95%, rgba(255, 218, 44, 0.8) 96.24%)',
      md: 'linear-gradient(356.38deg, rgba(255, 218, 44, 0) 69.1%, rgba(255, 218, 44, 0.3) 79.95%, rgba(255, 218, 44, 0.8) 96.24%)',
    },
    iconBg:
      'linear-gradient(304.94deg, rgba(255, 204, 0, 0.06) 0%, rgba(255, 204, 0, 0.12) 50%, rgba(255, 204, 0, 0.06) 100%)',
    iconBorderColor:
      'linear-gradient(91.01deg, rgba(229, 186, 85, 0.6) 0.87%, rgba(229, 186, 85, 0) 30.35%, rgba(229, 186, 85, 0) 69.65%, rgba(229, 186, 85, 0.6) 99.13%)',  
  },
  grey: {
    overlayBg:
      'linear-gradient(90deg, rgba(215, 215, 215, 0.2) 0%, rgba(215, 215, 215, 0) 50%)',
    iconBg:
      'linear-gradient(302.62deg, rgba(224, 224, 224, 0.06) 0%, rgba(224, 224, 224, 0.12) 50%, rgba(224, 224, 224, 0.06) 100%)',
    iconBorderColor:
      'linear-gradient(91.01deg, rgba(224, 224, 224, 0.6) 0.87%, rgba(224, 224, 224, 0) 30.35%, rgba(224, 224, 224, 0) 69.65%, rgba(224, 224, 224, 0.6) 99.13%)',
  },
}

type FullScreenModalProps = {
  title: string;
  children: React.ReactNode;
  closeButtonProps?: Omit<IconButtonProps, 'aria-label'>;
  modalVariant?: 'green' | 'blue' | 'red' | 'grey' | 'golden';
  icon?: React.ReactNode;
  boxShadow1?: string;
  boxShadow2?: string;
}

const FullscreenModal = ({
  title,
  children,
  closeButtonProps,
  modalVariant = 'green',
  icon = <IconInfo />,
  boxShadow1 = "0px 0px 250px 40px rgba(170, 170, 170, 0.6) inset" ,
  boxShadow2 = "0px 0px 250px 40px rgba(170, 170, 170, 0.6) inset",
  ...rest
}: ModalProps & FullScreenModalProps) => {
  return (
    <ChakraModal isCentered {...rest} size="full" variant="fullScreen">
      <ModalOverlay />
      <ModalContent zIndex={1500}>
        <Show above="md">
          <Box 
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"  
            background="darkVoid.100"
            boxShadow={boxShadow1}
            zIndex={-1}
            style={{
              mask: 'linear-gradient(205deg, rgba(255, 255, 255, 0) 50%, #FFFFFF 100%)',
              WebkitMask: 'linear-gradient(205deg, rgba(255, 255, 255, 0) 50%, #FFFFFF 100%)',
            }}
          />
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            background="darkVoid.100"
            zIndex={-1}
            boxShadow={boxShadow2}
            style={{
              mask: 'linear-gradient(150deg, rgba(255, 255, 255, 0) 50%, #FFFFFF 100%)',
              WebkitMask: 'linear-gradient(150deg, rgba(255, 255, 255, 0) 50%, #FFFFFF 100%)',
            }}
          />
        </Show>

        <Show below="md">
          <Box 
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"  
            background="darkVoid.100"
            zIndex={-1}
          />
        </Show>
        <ModalHeader>
          <Box 
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            background={modalVariantColors[modalVariant].overlayBg}
            borderRadius={{ base: '0', md: '8px' }}
            zIndex={0}
          />
          <Flex
            zIndex={1}
            pl={{ base: '2', md: '0' }}
          >
            {title}
          </Flex>
          <Flex
            position="relative"
            p={{ base: '1.5', md: '1.5' }}
            mr={{ base: '0.5rem', md: '1rem' }}
            _before={{
              content: '""',
              position: 'absolute',
              inset: 0,
              background: modalVariantColors[modalVariant].iconBorderColor,
              borderRadius: '4px',
              padding: '2px',
              mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
              maskComposite: 'exclude',
            }}
          >
            <Flex
              background={modalVariantColors[modalVariant].iconBg}
              borderRadius="4px"
              px={{ base: '0.5rem', md: '1rem' }}
              py={{ base: '0.25rem', md: '0.5rem' }}
            >
              {icon}
            </Flex>
          </Flex>
          <IconButton
            aria-label="Close modal"
            icon={<IconClose />}
            position="absolute"
            variant="unstyled"
            alignItems="center"
            justifyContent="center"
            display="flex"
            background= 'rgba(255, 255, 255, 0.1)'
            _hover={{
              background: 'rgba(255, 255, 255, 0.2)',
            }}
            transition="background 0.2s ease-in-out"
            left={{ base: '4', md: '7' }}
            top="50%"
            transform="translateY(-50%)"
            borderRadius="4px"
            width={{ base: '30px', md: '60px' }}
            height={{ base: '36px', md: '46px' }}
            onClick={closeButtonProps?.onClick ?? rest.onClose}
            zIndex={1}
            {...closeButtonProps}
          />
        </ModalHeader>
        <ModalBody flexDir="column" w="full" h="full" p={{ base: '0', md: '4' }}  overflow="auto"> 
          {children}
        </ModalBody>
      </ModalContent>
    </ChakraModal>
  );
};

export default FullscreenModal;
